import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { QuestionSet, QuizMode } from '../../types';
import {
  Brain,
  Calculator,
  BookText,
  Globe,
  Atom,
  Award,
  BookOpen,
  Compass,
  ArrowRight,
  Clock,
  CheckCircle2,
  Play,
  Sparkles,
  HelpCircle,
  Timer,
  ChevronRight,
  Flame,
  Zap,
  Target,
  BarChart2,
  TrendingUp,
  RotateCcw,
} from 'lucide-react';
import { useToast } from '../ui/Toast';

const ICON_MAP: Record<string, React.ReactNode> = {
  Brain: <Brain className="w-5 h-5 text-[#1fb4ac]" />,
  Calculator: <Calculator className="w-5 h-5 text-[#8a6508]" />,
  BookText: <BookText className="w-5 h-5 text-[#1fb4ac]" />,
  Globe: <Globe className="w-5 h-5 text-[#20A66A]" />,
  Atom: <Atom className="w-5 h-5 text-[#1fb4ac]" />,
  Award: <Award className="w-5 h-5 text-[#8a6508]" />,
  BookOpen: <BookOpen className="w-5 h-5 text-[#1fb4ac]" />,
  Compass: <Compass className="w-5 h-5 text-[#1b8e88]" />,
};

const COLOR_MAP: Record<string, string> = {
  Brain: 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#1b8e88]',
  Calculator: 'bg-[#FFF7D9] border-[#EBC138]/40 text-[#8a6508]',
  BookText: 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#1b8e88]',
  Globe: 'bg-[#EAF8F1] border-[#20A66A]/40 text-[#20A66A]',
  Atom: 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#1b8e88]',
  Award: 'bg-[#FFF7D9] border-[#EBC138]/40 text-[#8a6508]',
  BookOpen: 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#1b8e88]',
  Compass: 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#1b8e88]',
};

export const StudentDashboard: React.FC = () => {
  const {
    currentUser,
    sectors,
    subjects,
    questionSets,
    questions,
    attempts,
    userStats,
    setCurrentView,
    setActiveQuestionSet,
    setLatestAttempt,
  } = useApp();
  const { showToast } = useToast();

  const [selectedSector, setSelectedSector] = useState<number | 'all'>('all');
  const [modeModalSet, setModeModalSet] = useState<QuestionSet | null>(null);

  // Filter subjects by sector
  const filteredSubjects = subjects.filter((subj) =>
    selectedSector === 'all' ? true : subj.sector_id === selectedSector
  );

  // Student metrics
  const myAttempts = currentUser ? attempts.filter((a) => a.user_id === currentUser.id) : [];

  const totalSolved = userStats ? userStats.totalQuestionsAnswered : myAttempts.reduce((acc, a) => acc + (a.correct_answers + a.wrong_answers), 0);
  const totalCorrect = userStats ? userStats.totalCorrect : myAttempts.reduce((acc, a) => acc + a.correct_answers, 0);
  const avgAccuracy = userStats ? userStats.avgAccuracy : (totalSolved > 0 ? Math.round((totalCorrect / totalSolved) * 100) : 0);

  // 1. Today's Goal: count answered questions (correct + wrong, excluding skipped)
  const todaySolved = userStats ? userStats.todaySolved : myAttempts.filter((a) => {
    const dateStr = a.completed_at || a.started_at;
    if (!dateStr) return false;
    const formatted = dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr;
    const d = new Date(formatted);
    if (isNaN(d.getTime())) return false;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const today = new Date();
    const tyyyy = today.getFullYear();
    const tmm = String(today.getMonth() + 1).padStart(2, '0');
    const tdd = String(today.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}` === `${tyyyy}-${tmm}-${tdd}`;
  }).reduce((acc, a) => acc + (a.correct_answers + a.wrong_answers), 0);

  const dailyTarget = 20;
  const todayProgressPct = Math.min(100, Math.round((todaySolved / dailyTarget) * 100));

  // 2. Real Streak Calculation
  const currentStreak = userStats ? userStats.currentStreak : 0;

  // Recommended set (e.g. set 1 or the most recent)
  const recommendedSet = questionSets[0] || null;
  const recentAttempt = myAttempts[0] || null;

  const handleStartQuiz = (qSet: QuestionSet, mode: QuizMode) => {
    setActiveQuestionSet(qSet);
    setModeModalSet(null);
    showToast(`Starting ${qSet.name} in ${mode === 'revision' ? 'Revision' : 'Test'} Mode`, 'info');
    if (mode === 'revision') {
      setCurrentView('quiz-revision');
    } else {
      setCurrentView('quiz-test');
    }
  };

  const handleQuickPractice = () => {
    if (recommendedSet) {
      handleStartQuiz(recommendedSet, 'revision');
    } else {
      showToast('No question sets available currently', 'error');
    }
  };

  const handleReviewAttempt = (attemptId: number) => {
    const attempt = attempts.find((a) => a.id === attemptId);
    if (attempt) {
      setLatestAttempt(attempt);
      setCurrentView('quiz-scorecard');
    }
  };

  // Dynamic greeting based on current time
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="space-y-6 sm:space-y-8 pb-24 md:pb-16">
      {/* 1. Light Modern Welcome Hero & Daily Progress */}
      <section
        id="student-hero-banner"
        className="rounded-2xl sm:rounded-3xl bg-white border border-[#E5E9E9] p-5 sm:p-7 shadow-[0_4px_20px_rgba(17,17,17,0.03)] relative overflow-hidden"
      >
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative z-10">
          <div className="max-w-2xl">
            {/* Streak Counter */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFF7D9] text-[#8a6508] text-xs font-bold border border-[#EBC138]/50">
                <Flame className="w-3.5 h-3.5 text-[#EBC138] fill-[#EBC138]" />
                {currentStreak} Day Streak
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#111111] tracking-tight mb-2">
              {getGreeting()}, {currentUser ? currentUser.name.split(' ')[0] : 'Student'} 👋
            </h1>
            <p className="text-[#5F6368] text-sm sm:text-base leading-relaxed mb-6 font-normal">
              Ready for a quick revision? Improve your accuracy by practicing a few questions with instant step-by-step solutions today.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              {recommendedSet && (
                <button
                  id="btn-quick-10min-practice"
                  onClick={handleQuickPractice}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#56E8E1] hover:bg-[#34d5cd] text-[#111111] font-bold text-sm shadow-xs transition-all cursor-pointer"
                >
                  <Zap className="w-4 h-4 text-[#111111] fill-[#111111]" />
                  Quick 10-Min Practice
                </button>
              )}

              {recentAttempt ? (
                <button
                  id="btn-continue-practice"
                  onClick={() => handleReviewAttempt(recentAttempt.id)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-[#F7FAFA] text-[#111111] font-bold text-sm border border-[#E5E9E9] shadow-2xs transition-all cursor-pointer"
                >
                  <RotateCcw className="w-4 h-4 text-[#1fb4ac]" />
                  Review Latest Score ({recentAttempt.score_percentage}%)
                </button>
              ) : (
                <button
                  id="btn-view-all-sets"
                  onClick={() => {
                    const el = document.getElementById('explore-subjects-section');
                    el?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white hover:bg-[#F7FAFA] text-[#111111] font-bold text-sm border border-[#E5E9E9] shadow-2xs transition-all cursor-pointer"
                >
                  <BookOpen className="w-4 h-4 text-[#1fb4ac]" />
                  Browse All Sets
                </button>
              )}
            </div>
          </div>

          {/* Compact Daily Progress Indicator Box */}
          <div className="bg-[#F7FAFA] rounded-2xl p-5 border border-[#E5E9E9] shadow-2xs w-full lg:w-80 shrink-0">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-[#1fb4ac]" />
                <span className="text-xs font-bold text-[#111111] uppercase tracking-wider">
                  Today's Goal
                </span>
              </div>
              <span className="text-xs font-black text-[#1b8e88]">
                {todaySolved} / {dailyTarget} MCQs
              </span>
            </div>

            <div className="w-full bg-[#E8FAF9] rounded-full h-2.5 overflow-hidden mb-2">
              <div
                className="h-full rounded-full bg-[#56E8E1] transition-all duration-500"
                style={{ width: `${Math.max(10, todayProgressPct)}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-xs text-[#5F6368]">
              <span>{todayProgressPct}% completed</span>
              <span>{Math.max(0, dailyTarget - todaySolved)} questions remaining</span>
            </div>
          </div>
        </div>

        {/* Decorative background aura */}
        <div className="absolute right-0 top-0 -mt-16 -mr-16 w-80 h-80 rounded-full bg-[#56E8E1]/10 blur-3xl pointer-events-none" />
      </section>

      {/* 2. Quick Practice Callout Card */}
      {recommendedSet && (
        <section
          id="quick-practice-callout"
          className="bg-white border border-[#56E8E1]/70 rounded-2xl p-5 sm:p-6 shadow-[0_4px_20px_rgba(17,17,17,0.03)] flex flex-col sm:flex-row sm:items-center justify-between gap-5 relative overflow-hidden"
        >
          <div className="relative z-10">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#E8FAF9] text-[#1b8e88] text-[11px] font-bold uppercase tracking-wider mb-2 border border-[#56E8E1]/40">
              <Zap className="w-3.5 h-3.5 fill-[#1fb4ac] text-[#1fb4ac]" />
              High-Yield Practice
            </div>
            <h2 className="text-xl font-bold tracking-tight text-[#111111] mb-1">
              {recommendedSet.name}
            </h2>
            <div className="flex flex-wrap items-center gap-3 text-xs text-[#5F6368] font-medium">
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {recommendedSet.time_limit_minutes} Minutes
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <HelpCircle className="w-3.5 h-3.5" />
                {questions.filter((q) => q.question_set_id === recommendedSet.id).length} Questions
              </span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-[#20A66A]" />
                Instant Step-by-Step Explanations
              </span>
            </div>
          </div>

          <div className="relative z-10 flex items-center gap-2">
            <button
              onClick={() => setModeModalSet(recommendedSet)}
              className="px-5 py-2.5 rounded-xl bg-[#56E8E1] text-[#111111] hover:bg-[#34d5cd] font-bold text-xs sm:text-sm transition-all shadow-xs flex items-center gap-1.5 cursor-pointer whitespace-nowrap"
            >
              Start Practice Now
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="absolute right-0 top-0 -mt-12 -mr-12 w-48 h-48 rounded-full bg-[#E8FAF9] blur-xl pointer-events-none" />
        </section>
      )}

      {/* 3. Performance Metric Cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Accuracy
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#EAF8F1] text-[#20A66A] flex items-center justify-center border border-[#20A66A]/30">
              <TrendingUp className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{avgAccuracy}%</div>
          <p className="text-xs text-[#5F6368] mt-1">Across all tests taken</p>
          <div className="w-full bg-[#EAF8F1] rounded-full h-1.5 mt-3 overflow-hidden">
            <div
              className="bg-[#20A66A] h-full rounded-full transition-all duration-500"
              style={{ width: `${avgAccuracy}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Questions Solved
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#E8FAF9] text-[#1fb4ac] flex items-center justify-center border border-[#56E8E1]/40">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{totalSolved}</div>
          <p className="text-xs text-[#5F6368] mt-1">{totalCorrect} correct answers</p>
          <div className="w-full bg-[#E8FAF9] rounded-full h-1.5 mt-3 overflow-hidden">
            <div
              className="bg-[#56E8E1] h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, (totalSolved / 50) * 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Sessions
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#FFF7D9] text-[#8a6508] flex items-center justify-center border border-[#EBC138]/40">
              <BookOpen className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{myAttempts.length}</div>
          <p className="text-xs text-[#5F6368] mt-1">Tests & revisions finished</p>
          <div className="w-full bg-[#FFF7D9] rounded-full h-1.5 mt-3 overflow-hidden">
            <div
              className="bg-[#EBC138] h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, (myAttempts.length / 10) * 100)}%` }}
            />
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-[#E5E9E9] shadow-[0_2px_8px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
              Curriculum Bank
            </span>
            <div className="w-8 h-8 rounded-xl bg-[#F0F4F4] text-[#5F6368] flex items-center justify-center border border-[#E5E9E9]">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl sm:text-3xl font-black text-[#111111]">{questions.length}</div>
          <p className="text-xs text-[#5F6368] mt-1">{questionSets.length} sets across {subjects.length} subjects</p>
          <div className="w-full bg-[#F0F4F4] rounded-full h-1.5 mt-3 overflow-hidden">
            <div className="bg-[#1fb4ac] h-full rounded-full" style={{ width: '100%' }} />
          </div>
        </div>
      </section>

      {/* 4. Subject Exploration with Sector Filter Chips */}
      <section id="explore-subjects-section" className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-[#111111] tracking-tight">
              Curriculum & Practice Modules
            </h2>
            <p className="text-xs sm:text-sm text-[#5F6368]">
              Select an exam stream or subject to start interactive practice or timed examinations.
            </p>
          </div>

          {/* Sector Filter Chips */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0 no-scrollbar">
            <button
              onClick={() => setSelectedSector('all')}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                selectedSector === 'all'
                  ? 'bg-[#111111] text-white shadow-xs'
                  : 'bg-white border border-[#E5E9E9] text-[#5F6368] hover:bg-[#F7FAFA]'
              }`}
            >
              All Sectors
            </button>
            {sectors.map((sector) => (
              <button
                key={sector.id}
                onClick={() => setSelectedSector(sector.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                  selectedSector === sector.id
                    ? 'bg-[#111111] text-white shadow-xs'
                    : 'bg-white border border-[#E5E9E9] text-[#5F6368] hover:bg-[#F7FAFA]'
                }`}
              >
                {sector.name}
              </button>
            ))}
          </div>
        </div>

        {/* Subjects & Question Sets Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredSubjects.map((subject) => {
            const subjectSets = questionSets.filter((qs) => qs.subject_id === subject.id);
            const subjectQuestions = questions.filter((q) =>
              subjectSets.some((s) => s.id === q.question_set_id)
            );
            const parentSector = sectors.find((sec) => sec.id === subject.sector_id);

            // Calculate subject progress from userStats
            const sStat = userStats?.subjectStats.find((s) => s.subject_id === subject.id || s.subject_name === subject.name);
            const subjectAttempts = myAttempts.filter((a) => a.subject_name === subject.name);
            const subjSolved = sStat ? sStat.solved : subjectAttempts.reduce((acc, a) => acc + a.total_questions, 0);
            const subjCorrect = sStat ? sStat.correct : subjectAttempts.reduce((acc, a) => acc + a.correct_answers, 0);
            const subjAccuracy = sStat ? sStat.accPct : (subjSolved > 0 ? Math.round((subjCorrect / subjSolved) * 100) : 0);

            const iconClass = COLOR_MAP[subject.icon] || 'bg-[#E8FAF9] border-[#56E8E1]/40 text-[#1b8e88]';

            return (
              <div
                key={subject.id}
                className="bg-white rounded-2xl border border-[#E5E9E9] hover:border-[#56E8E1]/60 card-hover flex flex-col justify-between overflow-hidden shadow-[0_2px_12px_rgba(17,17,17,0.02)]"
              >
                <div className="p-5">
                  {/* Subject Header */}
                  <div className="flex items-start justify-between gap-3 mb-3.5">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center border ${iconClass}`}
                    >
                      {ICON_MAP[subject.icon] || <BookOpen className="w-5 h-5 text-[#1fb4ac]" />}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {parentSector && (
                        <span className="px-2 py-0.5 rounded-md bg-[#F0F4F4] text-[#5F6368] text-[10px] font-semibold">
                          {parentSector.name}
                        </span>
                      )}
                      {subjSolved > 0 && (
                        <span className="text-[10px] font-bold text-[#20A66A] bg-[#EAF8F1] px-2 py-0.5 rounded-full border border-[#20A66A]/30">
                          {subjAccuracy}% Accuracy
                        </span>
                      )}
                    </div>
                  </div>

                  <h3 className="text-base font-bold text-[#111111] mb-1">
                    {subject.name}
                  </h3>
                  <p className="text-xs text-[#5F6368] line-clamp-2 mb-4 leading-relaxed">
                    {subject.description}
                  </p>

                  {/* Available Sets */}
                  <div className="space-y-2 mb-2">
                    <div className="text-[11px] font-bold text-[#5F6368] uppercase tracking-wider">
                      Question Sets ({subjectSets.length})
                    </div>
                    {subjectSets.length === 0 ? (
                      <div className="text-xs text-[#5F6368] italic py-2">
                        No sets published yet in this subject.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {subjectSets.map((qSet) => {
                          const setCount = questions.filter((q) => q.question_set_id === qSet.id).length;
                          return (
                            <div
                              key={qSet.id}
                              className="p-3 rounded-xl bg-[#F7FAFA] hover:bg-[#E8FAF9]/40 border border-[#E5E9E9] transition-colors flex items-center justify-between gap-3 text-left group"
                            >
                              <div className="min-w-0 flex-1">
                                <div className="text-xs font-bold text-[#111111] truncate group-hover:text-[#1fb4ac] transition-colors">
                                  {qSet.name}
                                </div>
                                <div className="text-[10px] text-[#5F6368] flex items-center gap-2 mt-0.5">
                                  <span>{setCount} Questions</span>
                                  <span>•</span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-2.5 h-2.5" />
                                    {qSet.time_limit_minutes}m Exam
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => setModeModalSet(qSet)}
                                className="px-3 py-1.5 text-xs font-bold rounded-lg bg-white border border-[#E5E9E9] hover:border-[#56E8E1] hover:text-[#111111] text-[#111111] transition-colors shadow-2xs whitespace-nowrap cursor-pointer"
                              >
                                Practice
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Subject Bottom Bar */}
                <div className="px-5 py-3 bg-[#F7FAFA] border-t border-[#E5E9E9] flex items-center justify-between text-xs text-[#5F6368]">
                  <span className="font-medium">{subjectQuestions.length} Total Questions</span>
                  {subjectSets.length > 0 && (
                    <button
                      onClick={() => setModeModalSet(subjectSets[0])}
                      className="font-bold text-[#1fb4ac] hover:text-[#1b8e88] flex items-center gap-1 cursor-pointer"
                    >
                      Start Set 1
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 5. Recent Practice History */}
      {myAttempts.length > 0 && (
        <section className="bg-white rounded-2xl border border-[#E5E9E9] p-5 sm:p-6 space-y-4 shadow-[0_2px_12px_rgba(17,17,17,0.02)]">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-[#111111] tracking-tight">
                Recent Practice & Exam Submissions
              </h2>
              <p className="text-xs text-[#5F6368]">
                Review past sessions, conceptual solutions, and analyze questions you missed.
              </p>
            </div>
            <button
              onClick={() => setCurrentView('student-analytics')}
              className="text-xs font-bold text-[#1fb4ac] hover:text-[#1b8e88] flex items-center gap-1 cursor-pointer"
            >
              All Analytics
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Desktop Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#F0F4F4] text-[#5F6368] border-b border-[#E5E9E9]">
                <tr>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Question Set</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Mode</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Score</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Breakdown</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Time Spent</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px]">Date</th>
                  <th className="py-2.5 px-3.5 font-bold uppercase tracking-wider text-[10px] text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E9E9]/60 text-[#111111]">
                {myAttempts.slice(0, 5).map((att) => (
                  <tr key={att.id} className="hover:bg-[#E8FAF9]/30 transition-colors">
                    <td className="py-3 px-3.5 font-semibold text-[#111111]">
                      {att.question_set_name || 'Practice Set'}
                    </td>
                    <td className="py-3 px-3.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full font-bold uppercase text-[10px] ${
                          att.mode === 'revision'
                            ? 'bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40'
                            : 'bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40'
                        }`}
                      >
                        {att.mode}
                      </span>
                    </td>
                    <td className="py-3 px-3.5 font-bold text-[#111111]">
                      <span
                        className={
                          att.score_percentage >= 70
                            ? 'text-[#20A66A]'
                            : att.score_percentage >= 50
                            ? 'text-[#8a6508]'
                            : 'text-[#D94A5C]'
                        }
                      >
                        {att.score_percentage}%
                      </span>
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">
                      <span className="text-[#20A66A] font-bold">{att.correct_answers}C</span> /{' '}
                      <span className="text-[#D94A5C] font-bold">{att.wrong_answers}W</span> /{' '}
                      <span className="text-[#5F6368]/70">{att.skipped_answers}S</span>
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">
                      {Math.floor(att.time_spent_seconds / 60)}m {att.time_spent_seconds % 60}s
                    </td>
                    <td className="py-3 px-3.5 text-[#5F6368]">
                      {new Date(att.completed_at).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </td>
                    <td className="py-3 px-3.5 text-right">
                      <button
                        onClick={() => handleReviewAttempt(att.id)}
                        className="px-2.5 py-1 text-xs font-bold rounded-lg bg-[#F0F4F4] hover:bg-[#E8FAF9] hover:text-[#1b8e88] text-[#111111] transition-colors cursor-pointer"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Stacked Card View */}
          <div className="sm:hidden space-y-2.5">
            {myAttempts.slice(0, 4).map((att) => (
              <div
                key={att.id}
                className="p-3.5 rounded-xl bg-[#F7FAFA] border border-[#E5E9E9] flex items-center justify-between gap-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span
                      className={`px-1.5 py-0.2 rounded text-[9px] font-bold uppercase tracking-wider ${
                        att.mode === 'revision'
                          ? 'bg-[#E8FAF9] text-[#1b8e88]'
                          : 'bg-[#FFF7D9] text-[#8a6508]'
                      }`}
                    >
                      {att.mode}
                    </span>
                    <span className="text-xs font-bold text-[#111111] truncate">
                      {att.question_set_name || 'Set'}
                    </span>
                  </div>
                  <div className="text-[11px] text-[#5F6368] flex items-center gap-2">
                    <span className="font-bold text-[#20A66A]">{att.correct_answers} Correct</span>
                    <span>•</span>
                    <span className="font-bold text-[#D94A5C]">{att.wrong_answers} Wrong</span>
                    <span>•</span>
                    <span>{Math.floor(att.time_spent_seconds / 60)}m</span>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span
                    className={`text-base font-black ${
                      att.score_percentage >= 70
                        ? 'text-[#20A66A]'
                        : att.score_percentage >= 50
                        ? 'text-[#8a6508]'
                        : 'text-[#D94A5C]'
                    }`}
                  >
                    {att.score_percentage}%
                  </span>
                  <button
                    onClick={() => handleReviewAttempt(att.id)}
                    className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-white border border-[#E5E9E9] text-[#111111] hover:border-[#56E8E1] hover:bg-[#E8FAF9]/40 shadow-2xs cursor-pointer"
                  >
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Mode Selection Modal: Revision Mode vs Test Mode */}
      {modeModalSet && (
        <div
          id="mode-selection-modal"
          className="fixed inset-0 z-50 bg-[#111111]/40 backdrop-blur-xs flex items-center justify-center p-4"
        >
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-[#E5E9E9] animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-[#1b8e88] bg-[#E8FAF9] px-2 py-0.5 rounded border border-[#56E8E1]/30">
                  Choose Practice Format
                </span>
                <h3 className="text-lg font-bold text-[#111111] mt-1">
                  {modeModalSet.name}
                </h3>
              </div>
              <button
                onClick={() => setModeModalSet(null)}
                className="text-[#5F6368] hover:text-[#111111] p-1 rounded-lg hover:bg-[#F0F4F4] transition-colors cursor-pointer"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-[#5F6368] mb-6 leading-relaxed">
              {modeModalSet.instructions || modeModalSet.description}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-6">
              {/* Option 1: Revision Mode */}
              <button
                id="select-revision-mode-btn"
                onClick={() => handleStartQuiz(modeModalSet, 'revision')}
                className="p-4 rounded-2xl border-2 border-[#E5E9E9] hover:border-[#56E8E1] hover:bg-[#E8FAF9]/30 text-left transition-all group flex flex-col justify-between cursor-pointer"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-[#E8FAF9] text-[#1b8e88] border border-[#56E8E1]/40 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                    <BookOpen className="w-5 h-5 text-[#1fb4ac]" />
                  </div>
                  <h4 className="text-sm font-bold text-[#111111] mb-1">
                    Revision Mode
                  </h4>
                  <p className="text-[11px] text-[#5F6368] leading-relaxed">
                    Instant answer feedback, step-by-step solutions, and conceptual explanations for deep learning.
                  </p>
                </div>
                <div className="mt-4 pt-2.5 border-t border-[#E5E9E9] flex items-center gap-1 text-xs font-bold text-[#1fb4ac]">
                  Start Practice
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>

              {/* Option 2: Timed Test Mode */}
              <button
                id="select-test-mode-btn"
                onClick={() => handleStartQuiz(modeModalSet, 'test')}
                className="p-4 rounded-2xl border-2 border-[#E5E9E9] hover:border-[#EBC138] hover:bg-[#FFF7D9]/30 text-left transition-all group flex flex-col justify-between cursor-pointer"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-[#FFF7D9] text-[#8a6508] border border-[#EBC138]/40 flex items-center justify-center mb-3 group-hover:scale-105 transition-transform">
                    <Timer className="w-5 h-5 text-[#8a6508]" />
                  </div>
                  <h4 className="text-sm font-bold text-[#111111] mb-1">
                    Timed Exam Mode
                  </h4>
                  <p className="text-[11px] text-[#5F6368] leading-relaxed">
                    {modeModalSet.time_limit_minutes}m countdown timer, question palette navigation, and final scorecard.
                  </p>
                </div>
                <div className="mt-4 pt-2.5 border-t border-[#E5E9E9] flex items-center gap-1 text-xs font-bold text-[#8a6508]">
                  Begin Exam
                  <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setModeModalSet(null)}
                className="px-4 py-2 text-xs font-semibold text-[#5F6368] hover:text-[#111111] hover:bg-[#F0F4F4] rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
